import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import {
  useCertifyBatch,
  useCurriculumCertificationRows,
  useCurriculumCertificationSummary,
  useRecertifyLesson,
  type CertificationFilters,
} from "@/hooks/useCurriculumCertification";

const STATUSES = ["all", "CERTIFIED", "REQUIRES_FIX", "BLOCKED", "EXCLUDED_FIXTURE"] as const;
const MEDIA_FILTERS = [
  { value: "all", label: "Any media state" },
  { value: "complete", label: "Video + visual present" },
  { value: "missing_video", label: "Missing video" },
  { value: "missing_infographic", label: "Missing visual" },
] as const;

function statusVariant(status: string) {
  if (status === "CERTIFIED") return "default" as const;
  if (status === "BLOCKED") return "destructive" as const;
  return "secondary" as const;
}

export default function AdminCurriculumCertification() {
  const [filters, setFilters] = useState<CertificationFilters>({ status: "REQUIRES_FIX", media: "all" });
  const summaryQuery = useCurriculumCertificationSummary();
  const rowsQuery = useCurriculumCertificationRows(filters);
  const recertify = useRecertifyLesson();
  const batch = useCertifyBatch();

  const summary = summaryQuery.data;
  const courses = useMemo(
    () => (summary?.by_course ?? []).map((c) => c.course).filter(Boolean).sort(),
    [summary],
  );
  const deficiencyCodes = useMemo(
    () => Object.keys(summary?.by_deficiency ?? {}).sort(),
    [summary],
  );

  const runBatch = async (mode: "pending" | "failing" | "all") => {
    try {
      const result = await batch.mutateAsync({ limit: 250, mode });
      toast.success(`Checked ${result.processed} lessons — ${result.remaining} still queued`);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const runOne = async (lessonId: string) => {
    try {
      await recertify.mutateAsync(lessonId);
      toast.success("Lesson re-checked");
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <header className="space-y-1">
          <h1 className="font-heading text-2xl font-bold text-foreground">Curriculum certification</h1>
          <p className="text-sm text-muted-foreground">
            Every lesson checked against the media-optional quality standard. Video and visuals are
            tracked separately and never fail a lesson.
          </p>
        </header>

        {summaryQuery.isError && (
          <Card className="p-4 text-sm text-destructive">{getErrorMessage(summaryQuery.error)}</Card>
        )}

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: "Lessons", value: summary.total_lessons },
              { label: "Courses", value: summary.total_courses },
              { label: "Certified", value: summary.by_status?.CERTIFIED ?? 0 },
              { label: "Requires fix", value: summary.by_status?.REQUIRES_FIX ?? 0 },
              { label: "Blocked", value: summary.by_status?.BLOCKED ?? 0 },
              { label: "Excluded test fixtures", value: summary.by_status?.EXCLUDED_FIXTURE ?? 0 },
              { label: "Missing video", value: summary.missing_video },
              { label: "Missing visual", value: summary.missing_infographic },
            ].map((s) => (
              <Card key={s.label} className="p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</p>
                <p className="font-heading text-2xl font-bold text-foreground">{s.value}</p>
              </Card>
            ))}
          </div>
        )}

        <Card className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => runBatch("pending")} disabled={batch.isPending}>
              Check new or changed lessons
            </Button>
            <Button variant="secondary" onClick={() => runBatch("failing")} disabled={batch.isPending}>
              Re-check unfinished lessons
            </Button>
            <Button variant="outline" onClick={() => runBatch("all")} disabled={batch.isPending}>
              Re-check everything
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Batches are resumable: run them as many times as needed — nothing is duplicated.
          </p>
        </Card>

        <Card className="p-4 grid gap-3 md:grid-cols-5">
          <label className="text-sm space-y-1">
            <span className="text-muted-foreground">Status</span>
            <select
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.status ?? "all"}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span className="text-muted-foreground">Course</span>
            <select
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.course ?? "all"}
              onChange={(e) => setFilters((f) => ({ ...f, course: e.target.value }))}
            >
              <option value="all">All courses</option>
              {courses.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span className="text-muted-foreground">Deficiency</span>
            <select
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.deficiencyCode ?? "all"}
              onChange={(e) => setFilters((f) => ({ ...f, deficiencyCode: e.target.value }))}
            >
              <option value="all">Any</option>
              {deficiencyCodes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span className="text-muted-foreground">Media</span>
            <select
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.media ?? "all"}
              onChange={(e) => setFilters((f) => ({ ...f, media: e.target.value as CertificationFilters["media"] }))}
            >
              {MEDIA_FILTERS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span className="text-muted-foreground">Search lesson</span>
            <Input
              value={filters.search ?? ""}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Lesson name"
            />
          </label>
        </Card>

        <Card className="p-0 overflow-hidden">
          {rowsQuery.isLoading && <p className="p-4 text-sm text-muted-foreground">Loading lessons…</p>}
          {rowsQuery.isError && <p className="p-4 text-sm text-destructive">{getErrorMessage(rowsQuery.error)}</p>}
          {rowsQuery.data?.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">No lessons match these filters.</p>
          )}
          <ul className="divide-y divide-border">
            {(rowsQuery.data ?? []).map((row) => (
              <li key={row.lesson_id} className="p-4 flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                    {row.invalidated_at && <Badge variant="outline">Needs re-check</Badge>}
                    <span className="font-heading font-semibold text-foreground">
                      {row.lessons?.lesson_name ?? row.lesson_id}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {row.lessons?.course_name ?? "—"} · skill: {row.competency_key ?? "not set"} ·
                    {" "}video {row.has_video ? "yes" : "no"} · visual {row.has_infographic ? "yes" : "no"}
                  </p>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {(row.deficiencies ?? []).map((d) => (
                      <Badge
                        key={d.code}
                        variant={d.blocking === false ? "outline" : "secondary"}
                        title={d.detail}
                      >
                        {d.code}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runOne(row.lesson_id)}
                  disabled={recertify.isPending}
                >
                  Re-check
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
